import React, { useEffect, useRef } from 'react';
import {
    FMAccordionItemProps,
    FMAccordionProps,
} from '../../types/fm-accordion.types';
import './fm-accordion.scss';
import FMAccordionItem from './fm-accordion-item';
import DOMPurify from 'dompurify';

const FMAccordion = (props: FMAccordionProps) => {
    const { panels, handleClick, title } = props;
    const accordionRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const titleHasMarkup =
        typeof title === 'string' &&
        title?.trim()?.startsWith('<') &&
        title?.trim()?.endsWith('>');

    const closeOtherPanels = (currentPanelIndex: number) => {
        for (let i = 0; i < accordionRef.current.children.length; i++) {
            if (i !== currentPanelIndex) {
                accordionRef.current.children[i].removeAttribute('open');
            }
        }
    };

    const generateFAQSchema = (items: FMAccordionItemProps[]) => {
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map((item) => ({
                '@type': 'Question',
                name:
                    typeof item.title === 'string'
                        ? item.title
                        : item.plainTitle?.replace('[disclaimer]', '') || '',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        typeof item.panel === 'string'
                            ? item.panel
                            : (() => {
                                  if (React.isValidElement(item.panel)) {
                                      return (
                                          item.plainPanel?.replace(
                                              '[disclaimer]',
                                              ''
                                          ) || ''
                                      );
                                  }
                                  return item.panel
                                      ? JSON.stringify(item.panel)
                                      : '';
                              })(),
                },
            })),
        };
    };

    useEffect(() => {
        if (sectionRef.current && !props.hideSchema && panels?.length) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(generateFAQSchema(panels));
            sectionRef.current.appendChild(script);

            return () => {
                if (sectionRef.current && script.parentNode) {
                    script.remove();
                }
            };
        }
    }, [panels, props.hideSchema]);

    return (
        <section className='fm-accordion' ref={sectionRef}>
            {props.title && (
                <header className='fm-accordion__heading'>
                    {titleHasMarkup ? (
                        <section
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    props.title.toString()
                                ),
                            }}
                        />
                    ) : (
                        <h2 className='fm-accordion__title'>{props.title}</h2>
                    )}
                </header>
            )}
            <section className='fm-accordion__panels' ref={accordionRef}>
                {panels?.map((panel, index) => (
                    <FMAccordionItem
                        key={`fm-accordion-panel-${index}-${panel}`}
                        {...panel}
                        {...(handleClick ? { handleClick: handleClick } : {})}
                        closeOtherPanels={() => closeOtherPanels(index)}
                    />
                ))}
            </section>
        </section>
    );
};

export default FMAccordion;
